import { useCallback, type MutableRefObject } from "react";
import type { QuizFormData } from "@core/features/quiz";
import { Loader } from "@themes/pollinator/ui/Loader";
import { Type } from "@themes/pollinator/ui/Type";
import { type StepValidationState } from "@platform/features/onboarding";
import { QuizQuestionSet } from "../../components/QuestionSet";
import { useGetQuizResponse, useQuizProps } from "../../hooks";

export type Props = {
  quizId: number;
  /** Called when quiz is completed. Receives all collected form data. */
  onCompleteQuiz: (formData: QuizFormData) => void;
  /** Whether to show action buttons (defaults to true) */
  showActionButtons?: boolean;
  /** Callback when step validation state changes (for parent footer) */
  onStepStateChange?: (state: StepValidationState) => void;
  /** Ref to expose the submit handler to parent */
  submitRef?: MutableRefObject<(() => Promise<void> | void) | null>;
  /** Initial question index for restoring position */
  initialQuestionIndex?: number;
};

/**
 * Container component for Quiz.
 * Handles data fetching, quiz navigation logic, and renders the quiz UI.
 */
export function QuizContainer({
  quizId,
  onCompleteQuiz,
  showActionButtons = true,
  onStepStateChange,
  submitRef,
  initialQuestionIndex,
}: Props) {
  const { quiz, quizResponse, isLoading } = useGetQuizResponse({ quizId });

  const {
    activeQuestionSet,
    buttonLabel,
    filterQuestionSet,
    handleNextQuestionObject,
    handlePreviousQuestionObject,
    isSubmitting,
    title,
    description,
    formData,
    stepIndex,
    totalSteps,
  } = useQuizProps({
    quiz,
    quizResponseId: quizResponse?.id,
    onCompleteQuiz,
    initialQuestionIndex,
  });

  // Check if this is the last quiz question
  const isLastQuestion = stepIndex === totalSteps - 1;
  // Check if we can go back to a previous question
  const canGoBackInQuiz = stepIndex > 0;

  // Handle validation state changes from the quiz form
  const handleStateChange = useCallback(
    (state: { isValid: boolean; isSubmitting: boolean }) => {
      if (onStepStateChange) {
        onStepStateChange({
          isValid: state.isValid,
          isDirty: true,
          isSubmitting: state.isSubmitting || isSubmitting,
          canProceed: state.isValid && !state.isSubmitting && !isSubmitting,
          // Quiz handles its own loading for internal navigation between questions
          // But on the last question, let parent show loading for step transition
          handlesOwnLoading: !isLastQuestion,
          // Expose back navigation for parent to use when quiz has internal steps
          onBack: canGoBackInQuiz ? handlePreviousQuestionObject : undefined,
          canGoBack: canGoBackInQuiz,
          // Quiz progress for parent footer to display "X of Y" for quiz questions
          quizStepIndex: stepIndex,
          quizTotalSteps: totalSteps,
        });
      }
    },
    [onStepStateChange, isSubmitting, isLastQuestion, canGoBackInQuiz, handlePreviousQuestionObject, stepIndex, totalSteps]
  );

  if (isLoading || !quiz || !quizResponse) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-[200px]">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 gap-2 md:gap-8">
      <div className="flex flex-col gap-4">
        <Type variant="h2">{title}</Type>
        <Type variant="body2">{description}</Type>
      </div>
      <div className="flex-1 flex items-center">
        <QuizQuestionSet
          questionSet={activeQuestionSet}
          filterQuestionSet={filterQuestionSet}
          buttonLabel={buttonLabel}
          onSubmit={handleNextQuestionObject}
          onBack={handlePreviousQuestionObject}
          isSubmitting={isSubmitting}
          defaultValues={formData}
          stepIndex={stepIndex}
          totalSteps={totalSteps}
          showActionButtons={showActionButtons}
          submitRef={submitRef}
          onStateChange={handleStateChange}
        />
      </div>
    </div>
  );
}

export default QuizContainer;
